/* oxlint-disable next/no-img-element -- Selected local files use temporary object URLs. */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type SyntheticEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  FULL_CROP,
  SCAN_LIMITS,
  clamp,
  detectDocumentCrop,
  formatBytes,
  nextRotation,
  normalizeCrop,
  numberedJpegName,
  orientedSize,
  outputSize,
  sanitizeBaseName,
  validateImageFile,
  type CropRect,
  type Rotation,
} from './lib/scan';
import { loadIdeaIdentity, sendAppIdea } from './lib/idea-api';
import { trackPageView } from './lib/analytics';

type AccountStatus = {
  connected: boolean;
  configured: boolean;
  displayName?: string;
  successfulUploads: number;
  remainingUploads: number;
  entitled: boolean;
  billingConfigured: boolean;
  canManageBilling: boolean;
  destinationPath: string | null;
  limits?: { perFileBytes: number };
};

type DropboxFolder = { name: string; path: string };
type OutputFormat = 'pdf' | 'jpg';

type ScanPage = {
  id: string;
  file: File;
  url: string;
  width: number;
  height: number;
  rotation: Rotation;
  crop: CropRect;
  cropMode: 'original' | 'auto' | 'manual';
  enhance: boolean;
  grayscale: boolean;
};

type PreparedFile = {
  blob: Blob;
  url: string;
  name: string;
  width?: number;
  height?: number;
};

type DecodedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  close(): void;
};

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';
type CropDrag = { mode: DragMode; startX: number; startY: number; startCrop: CropRect };

const initialStatus: AccountStatus = {
  connected: false,
  configured: false,
  successfulUploads: 0,
  remainingUploads: 5,
  entitled: false,
  billingConfigured: false,
  canManageBilling: false,
  destinationPath: null,
};

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || 'https://receipt.bucksimple.com';
const apiUrl = (path: string) => new URL(path, API_ORIGIN).toString();
const dropboxStartUrl = () => {
  const returnTo = `${window.location.origin}${window.location.pathname}`;
  return `${apiUrl('/api/dropbox/start')}?returnTo=${encodeURIComponent(returnTo)}`;
};
const uploadEndpoint = import.meta.env.VITE_SCAN_UPLOAD_ENDPOINT || apiUrl('/api/upload');
const makeId = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const yieldToBrowser = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

function Icon({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

const icons = {
  camera: <Icon size={25}><path d="M4 8h3l1.6-2h6.8L17 8h3v11H4z" /><circle cx="12" cy="13.5" r="3.5" /></Icon>,
  photo: <Icon><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="9" r="1.4" /><path d="m5.5 17 4.2-4.2 3.1 3.1 2.2-2.2 3.5 3.3" /></Icon>,
  rotate: <Icon><path d="M5 8a8 8 0 1 1-1 5" /><path d="M5 4v4h4" /></Icon>,
  crop: <Icon><path d="M7 3v14a2 2 0 0 0 2 2h12M3 7h14a2 2 0 0 1 2 2v12" /></Icon>,
  trash: <Icon><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></Icon>,
  arrowUp: <Icon><path d="m7 14 5-5 5 5" /></Icon>,
  arrowDown: <Icon><path d="m7 10 5 5 5-5" /></Icon>,
  folder: <Icon><path d="M3 6h7l2 2h9v11H3z" /></Icon>,
  folderPlus: <Icon><path d="M3 6h7l2 2h9v11H3zM12 11v5m-2.5-2.5h5" /></Icon>,
  lock: <Icon><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></Icon>,
  upload: <Icon><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" /><path d="M5 15v4h14v-4" /></Icon>,
  download: <Icon><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14" /></Icon>,
  check: <Icon><path d="m5 12 4 4L19 6" /></Icon>,
  close: <Icon><path d="m7 7 10 10M17 7 7 17" /></Icon>,
  install: <Icon><rect x="6" y="2.5" width="12" height="19" rx="3" /><path d="M12 6v8m0 0 3-3m-3 3-3-3M10 18h4" /></Icon>,
  logout: <Icon><path d="M10 5H5v14h5M13 8l4 4-4 4m4-4H9" /></Icon>,
  file: <Icon><path d="M7 3h7l4 4v14H7zM14 3v5h5M9.5 13h5M9.5 17h5" /></Icon>,
};

async function decodeImage(file: Blob): Promise<DecodedImage> {
  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch {
      try {
        const bitmap = await createImageBitmap(file);
        return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
      } catch {
        // Fall through for browsers with partial ImageBitmap support.
      }
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function applyRotation(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  rotation: Rotation,
) {
  if (rotation === 90) {
    context.translate(height, 0);
    context.rotate(Math.PI / 2);
  } else if (rotation === 180) {
    context.translate(width, height);
    context.rotate(Math.PI);
  } else if (rotation === 270) {
    context.translate(0, width);
    context.rotate(-Math.PI / 2);
  }
}

function imageFilter(page: ScanPage): string {
  const filters = [];
  if (page.enhance) filters.push('contrast(1.24)', 'brightness(1.04)');
  if (page.grayscale) filters.push('grayscale(1)');
  return filters.length ? filters.join(' ') : 'none';
}

function findCropInDecodedImage(decoded: DecodedImage, rotation: Rotation): CropRect | null {
  const oriented = orientedSize(decoded.width, decoded.height, rotation);
  const scale = Math.min(1, 360 / Math.max(oriented.width, oriented.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(24, Math.round(oriented.width * scale));
  canvas.height = Math.max(24, Math.round(oriented.height * scale));
  const context = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
  if (!context) return null;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.save();
  context.scale(scale, scale);
  applyRotation(context, decoded.width, decoded.height, rotation);
  context.drawImage(decoded.source, 0, 0, decoded.width, decoded.height);
  context.restore();
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  return detectDocumentCrop(image.data, image.width, image.height);
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('This browser could not make the JPG.'))),
      'image/jpeg',
      0.9,
    );
  });
}

async function renderPage(page: ScanPage): Promise<{ blob: Blob; width: number; height: number }> {
  const decoded = await decodeImage(page.file);
  try {
    const oriented = orientedSize(decoded.width, decoded.height, page.rotation);
    const crop = normalizeCrop(page.crop);
    const size = outputSize(decoded.width, decoded.height, page.rotation, crop);
    const cropX = oriented.width * crop.x;
    const cropY = oriented.height * crop.y;
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Canvas is unavailable in this browser.');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.scale(size.scale, size.scale);
    context.translate(-cropX, -cropY);
    applyRotation(context, decoded.width, decoded.height, page.rotation);
    context.filter = imageFilter(page);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(decoded.source, 0, 0, decoded.width, decoded.height);
    context.restore();
    return { blob: await canvasToJpeg(canvas), width: canvas.width, height: canvas.height };
  } finally {
    decoded.close();
  }
}

async function drawEditor(canvas: HTMLCanvasElement, page: ScanPage, isCurrent: () => boolean) {
  const decoded = await decodeImage(page.file);
  try {
    if (!isCurrent()) return;
    const oriented = orientedSize(decoded.width, decoded.height, page.rotation);
    const scale = Math.min(1, 1100 / oriented.width, 780 / oriented.height);
    canvas.width = Math.max(1, Math.round(oriented.width * scale));
    canvas.height = Math.max(1, Math.round(oriented.height * scale));
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return;
    context.fillStyle = '#101a2d';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.scale(scale, scale);
    applyRotation(context, decoded.width, decoded.height, page.rotation);
    context.filter = imageFilter(page);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(decoded.source, 0, 0, decoded.width, decoded.height);
    context.restore();

    const crop = normalizeCrop(page.crop);
    const x = crop.x * canvas.width;
    const y = crop.y * canvas.height;
    const width = crop.width * canvas.width;
    const height = crop.height * canvas.height;
    context.fillStyle = 'rgb(5 12 24 / 58%)';
    context.beginPath();
    context.rect(0, 0, canvas.width, canvas.height);
    context.rect(x, y, width, height);
    context.fill('evenodd');
    context.strokeStyle = page.cropMode === 'auto' ? '#53f0bc' : '#ffffff';
    context.lineWidth = Math.max(2, canvas.width / 380);
    context.strokeRect(x, y, width, height);
    context.strokeStyle = 'rgb(255 255 255 / 45%)';
    context.lineWidth = 1;
    for (const fraction of [1 / 3, 2 / 3]) {
      context.beginPath();
      context.moveTo(x + width * fraction, y);
      context.lineTo(x + width * fraction, y + height);
      context.stroke();
      context.beginPath();
      context.moveTo(x, y + height * fraction);
      context.lineTo(x + width, y + height * fraction);
      context.stroke();
    }
    const handleSize = Math.max(13, Math.min(canvas.width, canvas.height) * 0.035);
    context.fillStyle = page.cropMode === 'auto' ? '#53f0bc' : '#ffffff';
    for (const [handleX, handleY] of [[x, y], [x + width, y], [x, y + height], [x + width, y + height]]) {
      context.beginPath();
      context.arc(handleX, handleY, handleSize / 2, 0, Math.PI * 2);
      context.fill();
    }
  } finally {
    decoded.close();
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function App() {
  useEffect(() => {
    trackPageView('scan-to-dropbox');
  }, []);

  const [status, setStatus] = useState<AccountStatus>(initialStatus);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [folders, setFolders] = useState<DropboxFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('Scans');
  const [folderBusy, setFolderBusy] = useState(false);
  const [pages, setPages] = useState<ScanPage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [format, setFormat] = useState<OutputFormat>('pdf');
  const [filename, setFilename] = useState('my-scan');
  const [prepared, setPrepared] = useState<PreparedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [message, setMessage] = useState('');
  const [messageKind, setMessageKind] = useState<'plain' | 'error' | 'success'>('plain');
  const checkoutSucceeded = useMemo(
    () => new URLSearchParams(window.location.search).get('checkout') === 'success',
    [],
  );
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [suggestion, setSuggestion] = useState('');
  const [ideaEmail, setIdeaEmail] = useState('');
  const [ideaEmailFromDropbox, setIdeaEmailFromDropbox] = useState(false);
  const [ideaSending, setIdeaSending] = useState(false);
  const [ideaMessage, setIdeaMessage] = useState('Sent here in BuckSimple — no email app opens.');
  const cameraInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  const editorCanvas = useRef<HTMLCanvasElement>(null);
  const installDialog = useRef<HTMLDialogElement>(null);
  const previewUrls = useRef(new Set<string>());
  const preparedRef = useRef<PreparedFile[]>([]);
  const cropDrag = useRef<CropDrag | null>(null);
  const editorDrawToken = useRef(0);
  const settingsRef = useRef({ format, filename });
  const stateRef = useRef({ connected: status.connected, destinationPath: status.destinationPath, pageCount: pages.length });

  const selectedPage = pages.find((page) => page.id === selectedId) ?? pages[0] ?? null;
  const totalBytes = useMemo(() => pages.reduce((sum, page) => sum + page.file.size, 0), [pages]);
  const hasAccess = status.entitled || status.remainingUploads > 0;
  const currentStep = !status.connected ? 1 : !status.destinationPath ? 2 : 3;

  const clearPrepared = useCallback(() => {
    const hadPreparedFiles = preparedRef.current.length > 0;
    preparedRef.current.forEach((file) => URL.revokeObjectURL(file.url));
    preparedRef.current = [];
    if (hadPreparedFiles) setPrepared([]);
  }, []);

  const showMessage = useCallback((text: string, kind: 'plain' | 'error' | 'success' = 'plain') => {
    setMessage(text);
    setMessageKind(kind);
  }, []);

  const loadFolders = useCallback(async () => {
    setFolderBusy(true);
    showMessage('');
    try {
      const response = await fetch(apiUrl('/api/dropbox/folders'), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error('The secure Dropbox folder service is not connected yet.');
      }
      const result = (await response.json()) as { folders?: DropboxFolder[]; error?: string };
      if (!response.ok) throw new Error(result.error || 'Dropbox folders are unavailable.');
      const nextFolders = result.folders ?? [];
      setFolders(nextFolders);
      setSelectedFolder(nextFolders[0]?.path ?? '');
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Dropbox folders are unavailable.', 'error');
    } finally {
      setFolderBusy(false);
    }
  }, [showMessage]);

  useEffect(() => {
    let active = true;
    fetch(apiUrl('/api/status'), { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Dropbox setup is not connected.');
        }
        if (!response.ok) throw new Error('Dropbox status is unavailable.');
        return (await response.json()) as AccountStatus;
      })
      .then((nextStatus) => {
        if (!active) return;
        setStatus({ ...initialStatus, ...nextStatus });
        if (nextStatus.connected && !nextStatus.destinationPath) void loadFolders();
      })
      .catch(() => {
        if (!active) return;
        setStatus(initialStatus);
        showMessage('Dropbox setup has not been connected to this site yet.', 'error');
      })
      .finally(() => active && setLoadingStatus(false));

    const query = new URLSearchParams(window.location.search);
    const dropboxResult = query.get('dropbox');
    if (dropboxResult && dropboxResult !== 'connected') {
      queueMicrotask(() => showMessage(dropboxResult === 'cancelled'
        ? 'Dropbox login was cancelled.'
        : 'Dropbox login expired. Try again.', 'error'));
    }
    return () => { active = false; };
  }, [loadFolders, showMessage]);

  useEffect(() => {
    void loadIdeaIdentity().then((identity) => {
      if (!identity.email) return;
      setIdeaEmail(identity.email);
      setIdeaEmailFromDropbox(true);
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!checkoutSucceeded) return;
    let active = true;
    let timer: number | undefined;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(apiUrl('/api/status'), {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Status is unavailable.');
        const nextStatus = (await response.json()) as AccountStatus;
        if (!active) return;
        setStatus({ ...initialStatus, ...nextStatus });
        if (nextStatus.entitled) {
          showMessage('Your plan is active. You can keep saving scans.', 'success');
          return;
        }
      } catch {
        if (!active) return;
      }
      attempts += 1;
      if (attempts < 20) timer = window.setTimeout(() => void poll(), 1500);
      else showMessage('Payment received. Your plan is still activating; refresh this page in a moment.');
    };

    timer = window.setTimeout(() => {
      showMessage('Payment received. Your plan will be ready in a moment.');
      void poll();
    }, 1000);
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [checkoutSucceeded, showMessage]);

  useEffect(() => {
    const urls = previewUrls.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      preparedRef.current.forEach((file) => URL.revokeObjectURL(file.url));
    };
  }, []);

  useEffect(() => {
    if (!selectedPage || !editorCanvas.current) return;
    let active = true;
    const token = ++editorDrawToken.current;
    const canvas = editorCanvas.current;
    void drawEditor(canvas, selectedPage, () => active && editorDrawToken.current === token).catch(() => {
      if (active) showMessage('This page could not be drawn. Try another image.', 'error');
    });
    return () => { active = false; };
  }, [selectedPage, showMessage]);

  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const markInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', markInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', markInstalled);
    };
  }, []);

  useEffect(() => {
    settingsRef.current = { format, filename };
    stateRef.current = { connected: status.connected, destinationPath: status.destinationPath, pageCount: pages.length };
  }, [filename, format, pages.length, status.connected, status.destinationPath]);

  useEffect(() => {
    const context = document.modelContext;
    if (!context?.registerTool) return;
    const lifecycle = new AbortController();
    const register = (tool: Parameters<ModelContext['registerTool']>[0]) => {
      try {
        void Promise.resolve(context.registerTool(tool, { signal: lifecycle.signal })).catch(() => undefined);
      } catch {
        // WebMCP is optional; the visible workflow remains complete.
      }
    };

    register({
      name: 'get_scan_dropbox_setup',
      title: 'Check scan setup',
      description: 'Read the visible Dropbox connection, selected folder, and number of document pages ready to scan.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: false },
      execute: () => ({ ...stateRef.current, outputFormat: settingsRef.current.format }),
    });

    register({
      name: 'configure_scan_output',
      title: 'Configure scan output',
      description: 'Stage the visible output format or filename. The person must still choose pages and send them.',
      inputSchema: {
        type: 'object',
        properties: {
          format: { type: 'string', enum: ['pdf', 'jpg'] },
          filename: { type: 'string', minLength: 1, maxLength: 90 },
        },
        minProperties: 1,
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      execute: async (input) => {
        if (!isRecord(input)) throw new Error('Scan settings must be an object.');
        const keys = Object.keys(input);
        if (!keys.length || keys.some((key) => !['format', 'filename'].includes(key))) {
          throw new Error('Provide a supported scan setting.');
        }
        const nextFormat = input.format ?? settingsRef.current.format;
        const nextFilename = input.filename ?? settingsRef.current.filename;
        if (nextFormat !== 'pdf' && nextFormat !== 'jpg') throw new Error('format must be pdf or jpg.');
        if (typeof nextFilename !== 'string' || !nextFilename.trim() || nextFilename.length > 90) {
          throw new Error('filename must be 1–90 characters.');
        }
        clearPrepared();
        setFormat(nextFormat);
        setFilename(nextFilename.trim());
        settingsRef.current = { format: nextFormat, filename: nextFilename.trim() };
        await yieldToBrowser();
        return { configured: true, format: nextFormat, filename: sanitizeBaseName(nextFilename) };
      },
    });

    return () => lifecycle.abort();
  }, [clearPrepared]);

  async function addFiles(files: File[]) {
    if (!files.length || busy) return;
    setBusy(true);
    clearPrepared();
    showMessage('');
    const additions: ScanPage[] = [];
    const errors: string[] = [];
    let nextCount = pages.length;
    let nextBytes = totalBytes;

    for (const file of files) {
      const policyError = validateImageFile(file, nextCount, nextBytes);
      if (policyError) {
        errors.push(policyError);
        continue;
      }
      setProgress(`Checking ${file.name}…`);
      try {
        const decoded = await decodeImage(file);
        const megapixels = (decoded.width * decoded.height) / 1_000_000;
        const width = decoded.width;
        const height = decoded.height;
        const detectedCrop = width && height && megapixels <= SCAN_LIMITS.maxMegapixels
          ? findCropInDecodedImage(decoded, 0)
          : null;
        decoded.close();
        if (!width || !height || megapixels > SCAN_LIMITS.maxMegapixels) {
          errors.push(`${file.name}: use an image under ${SCAN_LIMITS.maxMegapixels} megapixels.`);
          continue;
        }
        const url = URL.createObjectURL(file);
        previewUrls.current.add(url);
        additions.push({
          id: makeId(), file, url, width, height, rotation: 0,
          crop: detectedCrop ?? { ...FULL_CROP },
          cropMode: detectedCrop ? 'auto' : 'original',
          enhance: true, grayscale: false,
        });
        nextCount += 1;
        nextBytes += file.size;
      } catch {
        errors.push(`${file.name}: this image could not be read.`);
      }
      await yieldToBrowser();
    }

    if (additions.length) {
      setPages((current) => [...current, ...additions]);
      setSelectedId(additions[0].id);
    }
    if (errors.length) showMessage(errors.join(' '), 'error');
    else if (additions.length) {
      const locked = additions.filter((page) => page.cropMode === 'auto').length;
      showMessage(locked
        ? `Edges locked on ${locked} ${locked === 1 ? 'page' : 'pages'} (Beta). Check each green outline before saving.`
        : `${additions.length} ${additions.length === 1 ? 'page' : 'pages'} added. Auto-crop kept the original where edges were unclear.`);
    }
    setProgress('');
    setBusy(false);
  }

  function onFileInput(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void addFiles(files);
  }

  async function autoCropPage(page: ScanPage) {
    if (busy) return;
    setBusy(true);
    setProgress('Locking onto page edges (Beta)…');
    showMessage('');
    try {
      const decoded = await decodeImage(page.file);
      try {
        const crop = findCropInDecodedImage(decoded, page.rotation);
        if (!crop) {
          showMessage('Auto-crop (Beta) could not confidently find all four edges. The original is unchanged — drag the white corners if needed.');
          return;
        }
        updatePage(page.id, (current) => ({ ...current, crop, cropMode: 'auto' }));
        showMessage('Edges locked (Beta). Check the green outline and adjust any corner before saving.', 'success');
      } finally {
        decoded.close();
      }
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Auto-crop could not check this page.', 'error');
    } finally {
      setProgress('');
      setBusy(false);
    }
  }

  function updatePage(id: string, update: (page: ScanPage) => ScanPage) {
    clearPrepared();
    setPages((current) => current.map((page) => page.id === id ? update(page) : page));
  }

  function removePage(id: string) {
    if (busy) return;
    clearPrepared();
    setPages((current) => {
      const targetIndex = current.findIndex((page) => page.id === id);
      const target = current[targetIndex];
      if (target) {
        URL.revokeObjectURL(target.url);
        previewUrls.current.delete(target.url);
      }
      const next = current.filter((page) => page.id !== id);
      setSelectedId(next[Math.min(targetIndex, next.length - 1)]?.id ?? null);
      return next;
    });
  }

  function movePage(id: string, direction: -1 | 1) {
    if (busy) return;
    clearPrepared();
    setPages((current) => {
      const from = current.findIndex((page) => page.id === id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moving] = next.splice(from, 1);
      next.splice(to, 0, moving);
      return next;
    });
  }

  function pointerPosition(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = event.currentTarget;
    const bounds = canvas.getBoundingClientRect();
    return {
      x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
      y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
    };
  }

  function onCropStart(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!selectedPage || busy) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointerPosition(event);
    const crop = normalizeCrop(selectedPage.crop);
    const threshold = 0.075;
    const corners: Array<{ mode: DragMode; x: number; y: number }> = [
      { mode: 'nw', x: crop.x, y: crop.y },
      { mode: 'ne', x: crop.x + crop.width, y: crop.y },
      { mode: 'sw', x: crop.x, y: crop.y + crop.height },
      { mode: 'se', x: crop.x + crop.width, y: crop.y + crop.height },
    ];
    const corner = corners.find((candidate) => Math.hypot(point.x - candidate.x, point.y - candidate.y) <= threshold);
    const inside = point.x >= crop.x && point.x <= crop.x + crop.width
      && point.y >= crop.y && point.y <= crop.y + crop.height;
    cropDrag.current = {
      mode: corner?.mode ?? (inside ? 'move' : 'se'),
      startX: point.x,
      startY: point.y,
      startCrop: crop,
    };
  }

  function onCropMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!selectedPage || !cropDrag.current || busy) return;
    event.preventDefault();
    const point = pointerPosition(event);
    const drag = cropDrag.current;
    const dx = point.x - drag.startX;
    const dy = point.y - drag.startY;
    const start = drag.startCrop;
    let next = start;
    const minimum = 0.1;

    if (drag.mode === 'move') {
      next = { ...start, x: clamp(start.x + dx, 0, 1 - start.width), y: clamp(start.y + dy, 0, 1 - start.height) };
    } else {
      const left = drag.mode === 'nw' || drag.mode === 'sw'
        ? clamp(start.x + dx, 0, start.x + start.width - minimum)
        : start.x;
      const top = drag.mode === 'nw' || drag.mode === 'ne'
        ? clamp(start.y + dy, 0, start.y + start.height - minimum)
        : start.y;
      const right = drag.mode === 'ne' || drag.mode === 'se'
        ? clamp(start.x + start.width + dx, start.x + minimum, 1)
        : start.x + start.width;
      const bottom = drag.mode === 'sw' || drag.mode === 'se'
        ? clamp(start.y + start.height + dy, start.y + minimum, 1)
        : start.y + start.height;
      next = { x: left, y: top, width: right - left, height: bottom - top };
    }

    updatePage(selectedPage.id, (page) => ({ ...page, crop: normalizeCrop(next), cropMode: 'manual' }));
  }

  function finishCrop(event: ReactPointerEvent<HTMLCanvasElement>) {
    cropDrag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function onCropKey(event: ReactKeyboardEvent<HTMLCanvasElement>) {
    if (!selectedPage || busy || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const crop = normalizeCrop(selectedPage.crop);
    const amount = 0.01;
    let next = crop;
    if (event.shiftKey) {
      if (event.key === 'ArrowLeft') next = { ...crop, x: crop.x + amount, width: crop.width - amount };
      if (event.key === 'ArrowRight') next = { ...crop, width: crop.width - amount };
      if (event.key === 'ArrowUp') next = { ...crop, y: crop.y + amount, height: crop.height - amount };
      if (event.key === 'ArrowDown') next = { ...crop, height: crop.height - amount };
    } else {
      if (event.key === 'ArrowLeft') next = { ...crop, x: crop.x - amount };
      if (event.key === 'ArrowRight') next = { ...crop, x: crop.x + amount };
      if (event.key === 'ArrowUp') next = { ...crop, y: crop.y - amount };
      if (event.key === 'ArrowDown') next = { ...crop, y: crop.y + amount };
    }
    updatePage(selectedPage.id, (page) => ({ ...page, crop: normalizeCrop(next), cropMode: 'manual' }));
  }

  async function saveFolder(action: 'select' | 'create') {
    setFolderBusy(true);
    showMessage('');
    try {
      const payload = action === 'select' ? { action, path: selectedFolder } : { action, name: newFolderName };
      const response = await fetch(apiUrl('/api/dropbox/folders'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error('The secure Dropbox folder service is not connected yet.');
      }
      const result = (await response.json()) as { destinationPath?: string; error?: string };
      if (!response.ok || !result.destinationPath) throw new Error(result.error || 'The folder could not be saved.');
      setStatus((current) => ({ ...current, destinationPath: result.destinationPath as string }));
      showMessage(`Ready. Scans will go to ${result.destinationPath}.`, 'success');
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'The folder could not be saved.', 'error');
    } finally {
      setFolderBusy(false);
    }
  }

  async function prepareFiles(): Promise<PreparedFile[]> {
    if (!pages.length) throw new Error('Add at least one document page.');
    clearPrepared();
    const baseName = sanitizeBaseName(filename);
    const rendered: Array<{ blob: Blob; width: number; height: number }> = [];

    for (let index = 0; index < pages.length; index += 1) {
      setProgress(`Cleaning page ${index + 1} of ${pages.length}…`);
      await yieldToBrowser();
      rendered.push(await renderPage(pages[index]));
    }

    let nextFiles: PreparedFile[];
    if (format === 'jpg') {
      nextFiles = rendered.map((page, index) => ({
        blob: page.blob,
        url: URL.createObjectURL(page.blob),
        name: numberedJpegName(baseName, index, rendered.length),
        width: page.width,
        height: page.height,
      }));
    } else {
      setProgress('Building PDF…');
      const { PDFDocument, rgb } = await import('pdf-lib');
      const document = await PDFDocument.create();
      document.setTitle(baseName);
      document.setAuthor('BuckSimple');
      document.setCreator('BuckSimple Scan to Dropbox');
      for (const renderedPage of rendered) {
        const imageBytes = await renderedPage.blob.arrayBuffer();
        const image = await document.embedJpg(imageBytes);
        const landscape = renderedPage.width > renderedPage.height;
        const pageWidth = landscape ? 792 : 612;
        const pageHeight = landscape ? 612 : 792;
        const margin = 18;
        const scale = Math.min(
          (pageWidth - margin * 2) / renderedPage.width,
          (pageHeight - margin * 2) / renderedPage.height,
        );
        const drawWidth = renderedPage.width * scale;
        const drawHeight = renderedPage.height * scale;
        const page = document.addPage([pageWidth, pageHeight]);
        page.drawRectangle({ x: 0, y: 0, width: pageWidth, height: pageHeight, color: rgb(1, 1, 1) });
        page.drawImage(image, {
          x: (pageWidth - drawWidth) / 2,
          y: (pageHeight - drawHeight) / 2,
          width: drawWidth,
          height: drawHeight,
        });
      }
      const bytes = await document.save({ useObjectStreams: true });
      const copiedBytes = new Uint8Array(bytes.byteLength);
      copiedBytes.set(bytes);
      const blob = new Blob([copiedBytes.buffer], { type: 'application/pdf' });
      nextFiles = [{ blob, url: URL.createObjectURL(blob), name: `${baseName}.pdf` }];
    }

    preparedRef.current = nextFiles;
    setPrepared(nextFiles);
    setProgress('');
    return nextFiles;
  }

  async function makeFiles() {
    if (busy) return;
    setBusy(true);
    showMessage('');
    try {
      const files = await prepareFiles();
      showMessage(`${files.length === 1 ? files[0].name : `${files.length} JPG files`} ready.`, 'success');
    } catch (error) {
      setProgress('');
      showMessage(error instanceof Error ? error.message : 'The scan could not be made.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function uploadFiles() {
    if (busy || !status.connected || !status.destinationPath) return;
    if (!hasAccess) {
      showMessage('Your included saves are used. Continue to checkout below.', 'error');
      return;
    }
    setBusy(true);
    showMessage('');
    let completed = 0;
    try {
      const files = preparedRef.current.length ? preparedRef.current : await prepareFiles();
      const endpointUrl = new URL(uploadEndpoint, API_ORIGIN);
      if (endpointUrl.origin !== API_ORIGIN) {
        throw new Error('The upload address must use the secure BuckSimple service. Your finished file is still ready to download.');
      }
      const perFileLimit = status.limits?.perFileBytes ?? 40 * 1024 * 1024;
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        if (file.blob.size > perFileLimit) {
          throw new Error(`${file.name} is over the 40 MB Dropbox save limit. Download it instead or make a smaller scan.`);
        }
        setProgress(`Sending ${index + 1} of ${files.length} to Dropbox…`);
        const response = await fetch(endpointUrl.toString(), {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': file.blob.type,
            Accept: 'application/json',
            'X-BuckSimple-Filename': encodeURIComponent(file.name),
            'X-Receipt-Filename': encodeURIComponent(file.name),
          },
          body: file.blob,
        });
        if (!response.headers.get('content-type')?.includes('application/json')) {
          throw new Error('The secure Scan upload service is not connected yet. Your finished file is still ready to download.');
        }
        const result = (await response.json()) as {
          error?: string;
          path?: string;
          remainingUploads?: number;
          successfulUploads?: number;
          checkoutRequired?: boolean;
          quotaExceeded?: 'count' | 'bytes';
          resetAt?: number;
        };
        if (!response.ok) {
          if (response.status === 402 && result.checkoutRequired) {
            setStatus((current) => ({ ...current, remainingUploads: 0 }));
          }
          if (response.status === 429 && result.resetAt) {
            throw new Error(`${result.error || 'Your monthly plan limit is reached.'} It resets ${new Date(result.resetAt * 1000).toLocaleDateString()}.`);
          }
          if (response.status === 415 && file.blob.type === 'application/pdf') {
            throw new Error('PDF upload is not enabled on the server yet. Your PDF is ready to download.');
          }
          throw new Error(result.error || 'Dropbox did not confirm this save. Check the folder before trying again.');
        }
        completed += 1;
        setStatus((current) => ({
          ...current,
          successfulUploads: result.successfulUploads ?? current.successfulUploads + 1,
          remainingUploads: result.remainingUploads ?? Math.max(0, current.remainingUploads - 1),
        }));
      }
      showMessage(`${completed === 1 ? files[0].name : `${completed} JPG files`} saved to Dropbox.`, 'success');
    } catch (error) {
      const prefix = completed ? `${completed} file${completed === 1 ? '' : 's'} saved. ` : '';
      showMessage(prefix + (error instanceof Error ? error.message : 'The scan could not be saved.'), 'error');
    } finally {
      setProgress('');
      setBusy(false);
    }
  }

  async function startBilling(kind: 'checkout' | 'portal') {
    showMessage('');
    try {
      const response = await fetch(apiUrl(`/api/billing/${kind}`), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error('Billing is not connected yet.');
      }
      const result = (await response.json()) as { url?: string; error?: string };
      if (!response.ok || !result.url) throw new Error(result.error || 'Billing is unavailable.');
      window.location.assign(result.url);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Billing is unavailable.', 'error');
    }
  }

  async function disconnect() {
    showMessage('');
    try {
      const response = await fetch(apiUrl('/api/dropbox/disconnect'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Dropbox could not be disconnected.');
      setStatus((current) => ({ ...initialStatus, configured: current.configured, billingConfigured: current.billingConfigured }));
      setFolders([]);
      showMessage('Dropbox disconnected.', 'success');
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Dropbox could not be disconnected.', 'error');
    }
  }

  async function handleInstall() {
    if (installed) return;
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === 'accepted') setInstalled(true);
      setInstallPrompt(null);
      return;
    }
    installDialog.current?.showModal();
  }

  async function sendSuggestion(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const idea = suggestion.trim();
    if (!idea) {
      setIdeaMessage('Tell us the one job you want the app to do.');
      return;
    }
    if (!ideaEmail.trim()) {
      setIdeaMessage('Enter your email so Rob can reply.');
      return;
    }
    setIdeaSending(true);
    setIdeaMessage('Sending…');
    try {
      await sendAppIdea({ idea, replyEmail: ideaEmail });
      setSuggestion('');
      setIdeaMessage('Thanks — Rob has your idea.');
    } catch (error) {
      setIdeaMessage(error instanceof Error ? error.message : 'Your idea could not be sent. Please try again.');
    } finally {
      setIdeaSending(false);
    }
  }

  return (
    <>
      <header className="site-header">
        <a className="brand" href="https://bucksimple.com/" aria-label="BuckSimple home">
          <img className="brand-logo" src="/favicon.svg" alt="" width={40} height={40} />
          <span className="brand-wordmark">BuckSimple</span>
        </a>
        <button className="install-button" type="button" onClick={() => void handleInstall()} disabled={installed}>
          {installed ? icons.check : icons.install}
          {installed ? 'Installed' : 'Add to phone'}
        </button>
      </header>

      <main className="page-shell">
        <section className="intro" aria-labelledby="page-title">
          <div>
            <p className="eyebrow">SCAN · AUTO-CROP BETA · PDF OR JPG</p>
            <h1 id="page-title">Scan to Dropbox.</h1>
          </div>
          <p>Take a document photo. Clean it up. Save a PDF or JPG to your Dropbox folder.</p>
        </section>

        <nav className="steps" aria-label="Setup progress">
          {['Log in', 'Pick folder', 'Scan'].map((label, index) => {
            const step = index + 1;
            return (
              <div className={step === currentStep ? 'step current' : step < currentStep ? 'step done' : 'step'} key={label}>
                <span>{step < currentStep ? '✓' : step}</span><strong>{label}</strong>
              </div>
            );
          })}
        </nav>

        {loadingStatus ? (
          <section className="setup-card loading-card" aria-live="polite">
            <span className="spinner" />
            <h2>Checking Dropbox…</h2>
          </section>
        ) : !status.connected ? (
          <section className="setup-card login-card" aria-labelledby="login-heading">
            <span className="setup-icon">{icons.lock}</span>
            <p className="step-label">Step 1 of 3</p>
            <h2 id="login-heading">Log in with Dropbox</h2>
            <p>Dropbox handles your password. BuckSimple only gets permission to use its own app folder.</p>
            <button
              className="primary-button"
              type="button"
              disabled={!status.configured}
              onClick={() => window.location.assign(dropboxStartUrl())}
            >
              <span className="dropbox-diamonds" aria-hidden="true"><i /><i /><i /><i /></span>
              Continue with Dropbox
            </button>
            {!status.configured && <p className="setup-warning">Dropbox setup has not been connected to this site yet.</p>}
            <p className={`message ${messageKind}`} aria-live="polite">{message}</p>
            <p className="permission-line">{icons.lock} No BuckSimple password to remember.</p>
          </section>
        ) : !status.destinationPath ? (
          <section className="setup-card folder-card" aria-labelledby="folder-heading">
            <span className="setup-icon">{icons.folder}</span>
            <p className="step-label">Step 2 of 3</p>
            <h2 id="folder-heading">Pick a Dropbox folder</h2>
            <p>Use one inside Apps / BuckSimple, or make a new one.</p>
            {folderBusy ? <p className="folder-loading"><span className="spinner" /> Loading folders…</p> : (
              <div className="folder-controls">
                {folders.length > 0 && (
                  <div className="existing-folder">
                    <label htmlFor="existing-folder">Existing folder</label>
                    <select id="existing-folder" value={selectedFolder} onChange={(event) => setSelectedFolder(event.target.value)}>
                      {folders.map((folder) => <option value={folder.path} key={folder.path}>{folder.name}</option>)}
                    </select>
                    <button type="button" onClick={() => void saveFolder('select')} disabled={!selectedFolder}>Use this folder</button>
                  </div>
                )}
                <span className="or-line">or</span>
                <form onSubmit={(event) => { event.preventDefault(); void saveFolder('create'); }}>
                  <label htmlFor="new-folder">New folder</label>
                  <div>
                    <input id="new-folder" value={newFolderName} maxLength={80} onChange={(event) => setNewFolderName(event.target.value)} />
                    <button type="submit" disabled={!newFolderName.trim()}>{icons.folderPlus} Create</button>
                  </div>
                </form>
              </div>
            )}
            <p className={`message ${messageKind}`} aria-live="polite">{message}</p>
          </section>
        ) : (
          <section className="scan-workspace" aria-label="Document scanner">
            <div className="scan-main">
              <div className="workspace-heading">
                <div>
                  <p className="step-label">Step 3 of 3</p>
                  <h2>Add document pages</h2>
                </div>
                <span className="page-count">{pages.length} / {SCAN_LIMITS.maxPages}</span>
              </div>

              <div className="add-actions">
                <input ref={cameraInput} className="visually-hidden" tabIndex={-1} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={onFileInput} />
                <input ref={libraryInput} className="visually-hidden" tabIndex={-1} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onFileInput} />
                <button type="button" onClick={() => cameraInput.current?.click()} disabled={busy || pages.length >= SCAN_LIMITS.maxPages}>
                  <span>{icons.camera}</span><strong>Take photo</strong><small>Opens your camera</small>
                </button>
                <button type="button" onClick={() => libraryInput.current?.click()} disabled={busy || pages.length >= SCAN_LIMITS.maxPages}>
                  <span>{icons.photo}</span><strong>Choose photos</strong><small>JPG, PNG or WebP</small>
                </button>
              </div>

              {!pages.length ? (
                <div className="empty-scan">
                  <span>{icons.file}</span>
                  <strong>Your first page goes here.</strong>
                  <p>The camera opens only after you tap “Take photo.”</p>
                </div>
              ) : (
                <>
                  <div className={`editor${selectedPage?.cropMode === 'auto' ? ' edges-locked' : ''}`}>
                    <div className="edge-status" aria-live="polite">
                      {selectedPage?.cropMode === 'auto' ? <><span aria-hidden="true" /> Edges locked <b>Beta</b></> : <>Auto-crop can find and show the page edges.</>}
                    </div>
                    <p className="editor-help" id="crop-help">Use Auto-crop, then check the outline. Drag the corners to adjust. Drag inside to move.</p>
                    <canvas
                      ref={editorCanvas}
                      tabIndex={0}
                      aria-label={`Crop ${selectedPage?.file.name ?? 'selected page'}`}
                      aria-describedby="crop-help"
                      onPointerDown={onCropStart}
                      onPointerMove={onCropMove}
                      onPointerUp={finishCrop}
                      onPointerCancel={finishCrop}
                      onKeyDown={onCropKey}
                    />
                    {selectedPage && (
                      <div className="edit-actions">
                        <button className="auto-crop-button" type="button" onClick={() => void autoCropPage(selectedPage)} disabled={busy}>
                          {icons.crop} Auto-crop <span className="beta-label">Beta</span>
                        </button>
                        <button type="button" onClick={() => updatePage(selectedPage.id, (page) => ({ ...page, rotation: nextRotation(page.rotation), crop: { ...FULL_CROP }, cropMode: 'original' }))}>
                          {icons.rotate} Rotate
                        </button>
                        <button type="button" onClick={() => updatePage(selectedPage.id, (page) => ({ ...page, crop: { ...FULL_CROP }, cropMode: 'original' }))}>
                          {icons.crop} Original
                        </button>
                        <label className={selectedPage.enhance ? 'toggle checked' : 'toggle'}>
                          <input type="checkbox" checked={selectedPage.enhance} onChange={() => updatePage(selectedPage.id, (page) => ({ ...page, enhance: !page.enhance }))} />
                          <span /> Enhance
                        </label>
                        <label className={selectedPage.grayscale ? 'toggle checked' : 'toggle'}>
                          <input type="checkbox" checked={selectedPage.grayscale} onChange={() => updatePage(selectedPage.id, (page) => ({ ...page, grayscale: !page.grayscale }))} />
                          <span /> B&amp;W
                        </label>
                      </div>
                    )}
                  </div>

                  <ol className="page-list" aria-label="Scan pages">
                    {pages.map((page, index) => (
                      <li className={page.id === selectedPage?.id ? 'selected' : ''} key={page.id}>
                        <button className="page-preview" type="button" onClick={() => setSelectedId(page.id)} aria-label={`Edit page ${index + 1}`}>
                          <span>{index + 1}</span><img src={page.url} alt="" /><strong>{page.file.name}</strong>
                        </button>
                        <div className="page-actions">
                          <button type="button" aria-label={`Move page ${index + 1} up`} disabled={index === 0 || busy} onClick={() => movePage(page.id, -1)}>{icons.arrowUp}</button>
                          <button type="button" aria-label={`Move page ${index + 1} down`} disabled={index === pages.length - 1 || busy} onClick={() => movePage(page.id, 1)}>{icons.arrowDown}</button>
                          <button className="remove" type="button" aria-label={`Remove page ${index + 1}`} disabled={busy} onClick={() => removePage(page.id)}>{icons.trash}</button>
                        </div>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>

            <aside className="send-panel">
              <div className="dropbox-status">
                <span className="dropbox-diamonds" aria-hidden="true"><i /><i /><i /><i /></span>
                <div><small>Dropbox connected</small><strong>Apps / BuckSimple{status.destinationPath}</strong></div>
                <button type="button" onClick={() => { setStatus((current) => ({ ...current, destinationPath: null })); void loadFolders(); }}>Change</button>
              </div>

              <fieldset disabled={busy || !pages.length}>
                <legend>Save as</legend>
                <div className="format-choice">
                  <label className={format === 'pdf' ? 'selected' : ''}>
                    <input type="radio" name="format" value="pdf" checked={format === 'pdf'} onChange={() => { clearPrepared(); setFormat('pdf'); }} />
                    <strong>PDF</strong><span>One file</span>
                  </label>
                  <label className={format === 'jpg' ? 'selected' : ''}>
                    <input type="radio" name="format" value="jpg" checked={format === 'jpg'} onChange={() => { clearPrepared(); setFormat('jpg'); }} />
                    <strong>JPG</strong><span>One per page</span>
                  </label>
                </div>
              </fieldset>

              <label className="filename-field" htmlFor="filename">
                <span>File name</span>
                <div><input id="filename" value={filename} maxLength={90} disabled={busy} onChange={(event) => { clearPrepared(); setFilename(event.target.value); }} /><strong>.{format}</strong></div>
              </label>

              {progress && <p className="progress" aria-live="polite"><span className="spinner" /> {progress}</p>}
              <button className="make-button" type="button" disabled={busy || !pages.length} onClick={() => void makeFiles()}>
                {icons.check} {prepared.length ? 'Make files again' : 'Preview finished files'}
              </button>

              {prepared.length > 0 && (
                <div className="prepared-list">
                  {prepared.map((file) => (
                    <a href={file.url} download={file.name} key={file.name}>{icons.download}<span><strong>{file.name}</strong><small>{formatBytes(file.blob.size)}</small></span></a>
                  ))}
                </div>
              )}

              <button className="send-button" type="button" disabled={busy || !pages.length || !hasAccess} onClick={() => void uploadFiles()}>
                {icons.upload}<span><strong>{hasAccess ? 'Send to Dropbox' : 'Included saves used'}</strong><small>{status.entitled ? 'Plan active' : `${status.remainingUploads} included save${status.remainingUploads === 1 ? '' : 's'} left`}</small></span>
              </button>
              {!hasAccess && (
                <div className="billing-gate">
                  <p>Continue for $1/month, including up to 100 saves or 2 GiB per billing period. Cancel anytime.</p>
                  <button className="billing-button" type="button" disabled={!status.billingConfigured} onClick={() => void startBilling('checkout')}>Continue to secure checkout</button>
                </div>
              )}
              {status.canManageBilling && <button className="text-button" type="button" onClick={() => void startBilling('portal')}>Manage subscription</button>}
              <p className={`message ${messageKind}`} aria-live="polite">{message}</p>

              <div className="privacy-note">
                {icons.lock}
                <p><strong>Your originals stay untouched.</strong> Cropping and cleanup happen on this device. Only the finished copy is sent when you tap the button.</p>
              </div>
              <button className="disconnect-button" type="button" onClick={() => void disconnect()}>{icons.logout} Disconnect Dropbox</button>
            </aside>
          </section>
        )}

        <section className="plain-facts" aria-label="How Scan to Dropbox works">
          <article><strong>Camera permission</strong><p>Asked only after you tap “Take photo.” You can choose existing photos instead.</p></article>
          <article><strong>Dropbox permission</strong><p>Limited to the BuckSimple app folder. Your Dropbox password never reaches us.</p></article>
          <article><strong>Output choices</strong><p>Save the finished scan as one PDF or as separate JPG pages.</p></article>
        </section>

        <section className="suggestion">
          <div><p className="eyebrow">WANT ANOTHER SIMPLE APP?</p><h2>Tell us the one job it should do.</h2></div>
          <form onSubmit={sendSuggestion}>
            <label className="suggestion-email" htmlFor="app-idea-email">Your email
              <input id="app-idea-email" type="email" maxLength={254} required readOnly={ideaEmailFromDropbox} value={ideaEmail} onChange={(event) => setIdeaEmail(event.target.value)} placeholder="you@example.com" autoComplete="email" />
              {ideaEmailFromDropbox ? <span>Email filled automatically</span> : null}
            </label>
            <label className="visually-hidden" htmlFor="app-idea">Your BuckSimple app idea</label>
            <div><input id="app-idea" value={suggestion} onChange={(event) => setSuggestion(event.target.value)} placeholder="Example: sign a PDF and save it" maxLength={500} /><button type="submit" disabled={ideaSending}>{ideaSending ? 'Sending…' : 'Send idea'}</button></div>
            <small aria-live="polite">{ideaMessage}</small>
          </form>
        </section>
      </main>

      <footer>
        <a href="https://bucksimple.com/">BuckSimple</a>
        <span>A Breakpoint Industries Inc company</span>
        <nav><a href="https://bucksimple.com/privacy/">Privacy</a><a href="https://bucksimple.com/terms/">Terms</a></nav>
      </footer>

      <dialog className="install-dialog" ref={installDialog}>
        <div>
          <button className="dialog-close" type="button" aria-label="Close install instructions" onClick={() => installDialog.current?.close()}>{icons.close}</button>
          <img src="/app-icon.svg" alt="" width="70" height="70" />
          <p className="eyebrow">ADD TO HOME SCREEN</p>
          <h2>Use it like an app.</h2>
          <div className="install-steps">
            <section><strong>iPhone or iPad</strong><ol><li>Open this page in Safari.</li><li>Tap Share.</li><li>Tap “Add to Home Screen,” then Add.</li></ol></section>
            <section><strong>Android</strong><ol><li>Open this page in Chrome.</li><li>Tap the three-dot menu.</li><li>Tap “Install app” or “Add to Home screen.”</li></ol></section>
          </div>
          <button className="dialog-done" type="button" onClick={() => installDialog.current?.close()}>Got it</button>
        </div>
      </dialog>
    </>
  );
}
